import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { MEDIA } from "../data/media";
import { Database, Boxes, LayoutDashboard, Workflow, Users, Globe, Sparkles, BrainCircuit, Image as ImageIcon, Clapperboard, Megaphone } from "lucide-react";

const SERVICES = [
  { icon: Database, t: "Custom Business Platforms", d: "Replace spreadsheets and disconnected tools with one platform built around how you actually operate.", span: "md:col-span-2" },
  { icon: Workflow, t: "Workflow Automation", d: "Automate the repetitive work so your team spends time on what matters." },
  { icon: BrainCircuit, t: "AI Knowledge Bases", d: "Turn scattered knowledge into instant, reliable answers." },
  { icon: Sparkles, t: "AI-Powered Applications", d: "Modern AI-driven development, applied to real business problems.", span: "md:col-span-2" },
  { icon: LayoutDashboard, t: "Operational Dashboards", d: "See the whole business in one live, accurate view." },
  { icon: Boxes, t: "Internal Business Systems", d: "The connective tissue that ties your operations together." },
  { icon: Users, t: "Customer Portals", d: "A better experience for customers and employees alike." },
  { icon: Globe, t: "Websites", d: "Cinematic, high-converting websites — new builds or animation upgrades to your existing site.", span: "md:col-span-2" },
  { icon: ImageIcon, t: "AI Images", d: "On-brand product shots, campaign visuals and concept art — generated, edited and art-directed for your business." },
  { icon: Clapperboard, t: "AI Animation & Video", d: "Short cinematic brand films, animations and motion pieces produced with generative video." },
  { icon: Megaphone, t: "AI Video Ads", d: "Scroll-stopping AI-generated video ads for social and web — made fast, on-brand, and built to sell.", span: "md:col-span-2" },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      <Seo
        title="What's Possible — Platforms, Websites & AI Studio | AberCal"
        description="Custom business platforms, workflow automation, AI apps, websites, AI images, AI animation & video, and AI video ads — built for businesses in Calgary and beyond."
        path="/services"
      />
      <PageHero
        testid="services-hero"
        overline="What's Possible"
        title="Everything working together."
        accent="Not another subscription."
        subtitle="Everything here is part of a single platform, built around the way your business works — not another app to buy, log into and manage. One login, one source of truth, replacing spreadsheets, disconnected systems and expensive subscriptions."
        bg={MEDIA.tangle}
      />

      <section className="px-6 pb-40">
        <Reveal>
          <p className="mx-auto mb-14 max-w-3xl text-base leading-relaxed text-[var(--text-2)]">
            Some of these come together as one business platform. Others — websites, AI images, animation, video and AI video ads — we build as standalone projects for businesses. Either way, it's created specifically for you.
          </p>
        </Reveal>
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.t} delay={(i % 3) * 0.08} className={s.span || ""}>
              <div data-testid={`service-${i}`} className="card group relative h-full overflow-hidden p-9">
                <span className="absolute left-0 top-0 h-full w-[2px] origin-top scale-y-0 bg-[var(--red)] transition-transform duration-500 group-hover:scale-y-100" />
                <s.icon size={26} className="text-gold" strokeWidth={1.4} />
                <h3 className="mt-8 font-head text-3xl font-light">{s.t}</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-[var(--text-2)]">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mx-auto mt-20 flex max-w-7xl flex-col items-start justify-between gap-6 border-t border-[var(--border)] pt-14 sm:flex-row sm:items-center">
            <p className="font-head text-3xl font-light sm:text-4xl">Reduce complexity. Increase efficiency. Lower operating costs.</p>
            <Link to="/contact" className="btn-gold shrink-0">Start a Conversation</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
